import { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { API_BASE_URL } from './config';

interface CalendarModalProps {
  propertyId: number;
  onClose: () => void;
}

interface DateAvailability {
  [key: string]: boolean; // "2026-02-01": true/false
}

const CalendarModal = ({ propertyId, onClose }: CalendarModalProps) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [availability, setAvailability] = useState<DateAvailability>({});
  const [mode, setMode] = useState<'available' | 'unavailable'>('unavailable');
  const [loading, setLoading] = useState(false);

  // 날짜를 YYYY-MM-DD 형식으로 변환
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 월의 첫날과 마지막 날 가져오기
  const getMonthRange = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    return { firstDay, lastDay };
  };

  // 가용성 데이터 불러오기
  const fetchAvailability = async () => {
    const { firstDay, lastDay } = getMonthRange(currentMonth);
    const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    const { lastDay: nextMonthLastDay } = getMonthRange(nextMonth);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/properties/${propertyId}/date-availability?` +
        `start_date=${formatDate(firstDay)}&end_date=${formatDate(nextMonthLastDay)}`
      );
      const data = await response.json();
      setAvailability(data.availability || {});
    } catch (error) {
      console.error('Failed to fetch availability:', error);
    }
  };

  useEffect(() => {
    if (propertyId) {
      fetchAvailability();
    }
  }, [propertyId, currentMonth]);

  // 날짜 클릭 핸들러
  const handleDateClick = (date: Date) => {
    const dateStr = formatDate(date);
    const isSelected = selectedDates.some(d => formatDate(d) === dateStr);

    if (isSelected) {
      setSelectedDates(selectedDates.filter(d => formatDate(d) !== dateStr));
    } else {
      setSelectedDates([...selectedDates, date]);
    }
  };

  // 적용 버튼 핸들러
  const handleApply = async () => {
    if (selectedDates.length === 0) {
      alert('날짜를 선택해주세요.');
      return;
    }

    setLoading(true);

    try {
      const dates = selectedDates.map(d => formatDate(d));
      const response = await fetch(
        `${API_BASE_URL}/api/properties/${propertyId}/date-availability`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            dates: dates,
            is_available: mode === 'available'
          })
        }
      );

      if (response.ok) {
        alert('일정이 성공적으로 업데이트되었습니다.');
        setSelectedDates([]);
        fetchAvailability();
      } else {
        alert('일정 업데이트에 실패했습니다.');
      }
    } catch (error) {
      console.error('Failed to update availability:', error);
      alert('일정 업데이트에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 타일 클래스명 지정 (날짜 스타일링)
  const tileClassName = ({ date }: { date: Date }) => {
    const dateStr = formatDate(date);
    const isSelected = selectedDates.some(d => formatDate(d) === dateStr);
    const isUnavailable = availability[dateStr] === false;

    if (isSelected) {
      return 'selected-date';
    }
    if (isUnavailable) {
      return 'unavailable-date';
    }
    return '';
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '30px',
        maxWidth: '800px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        {/* 헤더 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h2 style={{ margin: 0, fontSize: '20px' }}>계약 가능 여부를 선택해 주세요</h2>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '0',
              width: '30px',
              height: '30px'
            }}
          >
            ×
          </button>
        </div>

        {/* 캘린더 */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Calendar
              onClickDay={handleDateClick}
              tileClassName={tileClassName}
              activeStartDate={currentMonth}
              onActiveStartDateChange={({ activeStartDate }) => {
                if (activeStartDate) {
                  setCurrentMonth(activeStartDate);
                }
              }}
              locale="ko-KR"
              formatDay={(locale, date) => date.getDate().toString()}
              formatMonthYear={(locale, date) => `${date.getFullYear()}년 ${date.getMonth() + 1}월`}
            />
            <Calendar
              onClickDay={handleDateClick}
              tileClassName={tileClassName}
              activeStartDate={new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)}
              locale="ko-KR"
              formatDay={(locale, date) => date.getDate().toString()}
              formatMonthYear={(locale, date) => `${date.getFullYear()}년 ${date.getMonth() + 1}월`}
              showNavigation={false}
            />
          </div>
        </div>

        {/* 선택 영역 */}
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
            날짜를 선택해 주세요
          </p>
          <div style={{ display: 'flex', gap: '15px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer'
            }}>
              <input
                type="radio"
                name="availability"
                checked={mode === 'available'}
                onChange={() => setMode('available')}
                style={{ cursor: 'pointer' }}
              />
              <span>계약 가능으로 설정</span>
            </label>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer'
            }}>
              <input
                type="radio"
                name="availability"
                checked={mode === 'unavailable'}
                onChange={() => setMode('unavailable')}
                style={{ cursor: 'pointer' }}
              />
              <span>계약 불가능으로 설정</span>
            </label>
          </div>
        </div>

        {/* 적용 버튼 */}
        <button
          onClick={handleApply}
          disabled={loading || selectedDates.length === 0}
          style={{
            width: '100%',
            padding: '15px',
            backgroundColor: selectedDates.length === 0 ? '#ccc' : '#000',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: selectedDates.length === 0 ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? '적용 중...' : '적용하기'}
        </button>

        <style>{`
          .selected-date {
            background-color: #6B4EFF !important;
            color: white !important;
          }
          .unavailable-date {
            text-decoration: line-through;
            color: #ff4444 !important;
          }
          .react-calendar {
            border: 1px solid #ddd;
            border-radius: 8px;
            padding: 10px;
          }
          .react-calendar__tile {
            padding: 10px;
            font-size: 14px;
          }
          .react-calendar__tile:enabled:hover {
            background-color: #f0f0f0;
          }
        `}</style>
      </div>
    </div>
  );
};

export default CalendarModal;
